import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import Cropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'
import { useAuth } from '../hooks/useAuth'
import { useAction, useMutation, useQuery } from 'convex/react'
import { useToast } from '../contexts/ToastContext'
import { useUI } from '../contexts/UIContext'
import { api } from '../convex_generated/api'
import { 
  UserCircleIcon, 
  PencilIcon,
  CameraIcon,
  CogIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  BellIcon,
  BanknotesIcon,
  ArrowDownTrayIcon,
  PlusCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'

interface _UserStats {
  branchesCount: number
  collaborationsCount: number
  purchasesCount: number
  averageRating: number
  totalRatingCount: number
}

interface UserPreferences {
  theme: 'light' | 'dark' | 'auto'
  fontSize: 'small' | 'medium' | 'large' | 'extra-large'
  notifications: {
    email: boolean
    push: boolean
    newContent: boolean
    collaborationUpdates: boolean
    paymentUpdates: boolean
  }
  reading: {
    autoSave: boolean
    lineSpacing: 'compact' | 'normal' | 'relaxed'
    fontFamily: 'serif' | 'sans-serif' | 'mono'
  }
}

const FONT_SIZE_TO_UI: Record<UserPreferences['fontSize'], import('../contexts/UIContext').ReadingFontSize> = {
  small: 'sm', medium: 'base', large: 'lg', 'extra-large': 'xl',
}
const UI_TO_FONT_SIZE: Record<import('../contexts/UIContext').ReadingFontSize, UserPreferences['fontSize']> = {
  sm: 'small', base: 'medium', lg: 'large', xl: 'extra-large',
}

/** Crop the image on a canvas and return a Blob. */
async function getCroppedBlob(imageSrc: string, crop: Area): Promise<Blob> {
  const image = new Image()
  image.crossOrigin = 'anonymous'
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve()
    image.onerror = reject
    image.src = imageSrc
  })
  const canvas = document.createElement('canvas')
  const size = Math.min(crop.width, crop.height, 512) // cap at 512px
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, size, size)
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Canvas export failed'))), 'image/jpeg', 0.9),
  )
}

const ProfilePage: React.FC = () => {
  const { user, isAuthenticated } = useAuth()
  const { showToast } = useToast()
  const { theme, setTheme, readingFontSize, setReadingFontSize } = useUI()
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'profile' | 'preferences' | 'stats' | 'analytics' | 'wallet'>('profile')
  const [searchParams, setSearchParams] = useSearchParams()
  
  const updateUserProfile = useMutation(api.auth.updateUserProfile)
  const generateAvatarUploadUrl = useMutation(api.users.generateAvatarUploadUrl)
  const updateAvatarFromStorage = useMutation(api.users.updateAvatarFromStorage)
  const createCheckoutSession = useAction(api.stripe.createCheckoutSession)
  const verifyCheckoutSession = useAction(api.stripe.verifyCheckoutSession)
  const createConnectAccount = useAction(api.stripe.createConnectAccount)
  const checkConnectStatus = useAction(api.stripe.checkConnectStatus)
  const getConnectDashboardLink = useAction(api.stripe.getConnectDashboardLink)
  const processWithdrawal = useAction(api.stripe.processWithdrawal)
  const stats = useQuery(api.auth.getUserStats, user ? { userId: user._id } : 'skip')
  const earnings = useQuery(api.payments.getMyEarnings)
  const analytics = useQuery(api.analytics.getAuthorAnalytics)
  const withdrawals = useQuery(api.payments.getMyWithdrawals)
  const followers = useQuery(api.users.getUserFollowers, user ? { userId: user._id, limit: 50 } : 'skip')
  const following = useQuery(api.users.getUserFollowing, user ? { userId: user._id, limit: 50 } : 'skip')

  // Avatar upload + crop
  const avatarFileRef = useRef<HTMLInputElement>(null)
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels)
  }, [])

  // Step 1: Pick file → show cropper
  const handleAvatarFilePick = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      showToast({ message: 'Please select an image file.', type: 'error' })
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast({ message: 'Image must be under 10 MB.', type: 'error' })
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      setCropImageSrc(reader.result as string)
      setCrop({ x: 0, y: 0 })
      setZoom(1)
    }
    reader.readAsDataURL(file)
    if (avatarFileRef.current) avatarFileRef.current.value = ''
  }, [showToast])

  // Step 2: Crop confirmed → upload
  const handleCropConfirm = useCallback(async () => {
    if (!cropImageSrc || !croppedAreaPixels) return
    setIsUploadingAvatar(true)
    try {
      const blob = await getCroppedBlob(cropImageSrc, croppedAreaPixels)
      const uploadUrl = await generateAvatarUploadUrl({})
      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'image/jpeg' },
        body: blob,
      })
      const { storageId } = await res.json()
      const result = await updateAvatarFromStorage({ storageId })
      if (result.avatarUrl) setAvatar(result.avatarUrl)
      showToast({ message: 'Profile photo updated!', type: 'success' })
    } catch (err) {
      showToast({ message: err instanceof Error ? err.message : 'Failed to upload photo.', type: 'error' })
    } finally {
      setIsUploadingAvatar(false)
      setCropImageSrc(null)
    }
  }, [cropImageSrc, croppedAreaPixels, generateAvatarUploadUrl, updateAvatarFromStorage, showToast])

  const handleCropCancel = useCallback(() => {
    setCropImageSrc(null)
  }, [])

  // Withdrawal form state
  const [withdrawAmount, setWithdrawAmount] = useState('')
  const [isWithdrawing, setIsWithdrawing] = useState(false)
  // Whole StoryCoins for the entered pounds. Rounded once, here: comparing
  // `pounds * 100` directly is off by float error (10.1 * 100 > 1010).
  const withdrawSC = Math.round(Number(withdrawAmount) * 100)
  // Buy StoryCoins form state
  const [buyAmount, setBuyAmount] = useState(500)
  const [isBuying, setIsBuying] = useState(false)
  // Stripe Connect state
  const [connectStatus, setConnectStatus] = useState<{ hasAccount: boolean; onboarded: boolean } | null>(null)
  const [connectLoading, setConnectLoading] = useState(false)

  // Handle Stripe Checkout success redirect and connect return
  useEffect(() => {
    const checkout = searchParams.get('checkout')
    const sessionId = searchParams.get('session_id')
    const connect = searchParams.get('connect')

    if (checkout === 'success' && sessionId) {
      setActiveTab('wallet')
      setIsBuying(true)
      setSearchParams({}, { replace: true })
      verifyCheckoutSession({ sessionId })
        .then((result) => {
          showToast({ message: `${result.amount} StoryCoins added to your account!`, type: 'success' })
        })
        .catch((e) => {
          showToast({ message: e instanceof Error ? e.message : 'Failed to verify payment', type: 'error' })
        })
        .finally(() => setIsBuying(false))
    } else if (checkout === 'cancelled') {
      setActiveTab('wallet')
      setSearchParams({}, { replace: true })
      showToast({ message: 'Purchase cancelled', type: 'info' })
    } else if (connect === 'return' || connect === 'refresh') {
      setActiveTab('wallet')
      setSearchParams({}, { replace: true })
      // Force re-check Connect status after onboarding
      setConnectStatus(null)
      checkConnectStatus()
        .then(setConnectStatus)
        .catch(() => setConnectStatus({ hasAccount: false, onboarded: false }))
    } else {
      const tab = searchParams.get('tab')
      if (tab === 'wallet' || tab === 'earnings' || tab === 'profile' || tab === 'preferences' || tab === 'stats' || tab === 'analytics') {
        // Support legacy 'earnings' param
        if (tab === 'earnings') { setActiveTab('wallet'); setSearchParams({}, { replace: true }); return }
        setActiveTab(tab)
      }
      // Auto-enable editing when navigated with ?edit=true
      if (searchParams.get('edit') === 'true') {
        setIsEditing(true)
        setActiveTab('profile')
        setSearchParams({}, { replace: true })
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Check Stripe Connect onboarding status when wallet tab is active
  useEffect(() => {
    if (activeTab === 'wallet' && !connectStatus) {
      checkConnectStatus()
        .then(setConnectStatus)
        .catch(() => setConnectStatus({ hasAccount: false, onboarded: false }))
    }
  }, [activeTab, connectStatus]) // eslint-disable-line react-hooks/exhaustive-deps
  
  // Form states
  const [displayName, setDisplayName] = useState(user?.displayName || '')
  const [avatar, setAvatar] = useState(user?.avatar || '')
  const [preferences, setPreferences] = useState<UserPreferences>(() => ({
    theme,
    fontSize: UI_TO_FONT_SIZE[readingFontSize],
    notifications: {
      email: true,
      push: true,
      newContent: true,
      collaborationUpdates: true,
      paymentUpdates: true
    },
    reading: {
      autoSave: true,
      lineSpacing: 'normal',
      fontFamily: 'serif'
    }
  }))

  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName)
      setAvatar(user.avatar || '')
    }
  }, [user])

  const handleSaveProfile = async () => {
    if (!user) return

    setIsLoading(true)
    try {
      const updatedUser = await updateUserProfile({
        displayName,
        avatar: avatar || undefined,
      })

      if (updatedUser) {
        showToast({ 
          message: 'Profile updated successfully!', 
          type: 'success' 
        })
        setIsEditing(false)
      }
    } catch (error) {
      showToast({ 
        message: error instanceof Error ? error.message : 'Failed to update profile', 
        type: 'error' 
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancelEdit = () => {
    setDisplayName(user?.displayName || '')
    setAvatar(user?.avatar || '')
    setIsEditing(false)
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <UserCircleIcon className="mx-auto h-12 w-12 text-neutral-400" />
          <h3 className="mt-2 text-sm font-medium text-neutral-900 dark:text-neutral-100">
            Please sign in to view your profile
          </h3>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
      <div className="container-max section-padding">
        <div className="max-w-4xl mx-auto">
          {/* Profile Header */}
          <div className="card p-8 mb-8">
            <div className="flex items-start space-x-6">
              <div className="relative">
                {avatar ? (
                  <img
                    src={avatar}
                    alt={displayName}
                    className="w-24 h-24 rounded-full object-cover border-4 border-white dark:border-neutral-700 shadow-lg"
                  />
                ) : (
                  <div className="w-24 h-24 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center border-4 border-white dark:border-neutral-700 shadow-lg">
                    <span className="text-2xl font-bold text-white">
                      {displayName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <input
                  ref={avatarFileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarFilePick}
                />
                {isEditing && (
                  <button
                    onClick={() => avatarFileRef.current?.click()}
                    disabled={isUploadingAvatar}
                    className="absolute -bottom-2 -right-2 bg-primary-600 text-white p-2 rounded-full shadow-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {isUploadingAvatar ? (
                      <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
                    ) : (
                      <CameraIcon className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    {isEditing ? (
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        className="text-2xl font-bold bg-transparent border-b-2 border-primary-500 text-neutral-900 dark:text-neutral-100 focus:outline-none"
                      />
                    ) : (
                      <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                        {displayName}
                      </h1>
                    )}
                    <p className="text-neutral-600 dark:text-neutral-400">
                      @{user.username} • {user.role}
                    </p>
                  </div>
                  
                  {isEditing ? (
                    <div className="space-x-3">
                      <button
                        onClick={handleCancelEdit}
                        className="px-4 py-2 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveProfile}
                        disabled={isLoading}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                      >
                        {isLoading ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setIsEditing(true); setActiveTab('profile') }}
                      className="px-4 py-2 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center"
                    >
                      <PencilIcon className="w-4 h-4 mr-2" />
                      Edit Profile
                    </button>
                  )}
                </div>

                <div className="flex items-center flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-600 dark:text-neutral-400">
                  <Link to={`/user/${user.username}`} className="flex items-center hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                    <UserGroupIcon className="w-4 h-4 mr-1" />
                    <span className="font-semibold text-neutral-900 dark:text-neutral-100">{followers?.total ?? 0}</span>&nbsp;Followers
                  </Link>
                  <Link to={`/user/${user.username}`} className="flex items-center hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                    <span className="font-semibold text-neutral-900 dark:text-neutral-100">{following?.total ?? 0}</span>&nbsp;Following
                  </Link>
                  <div className="flex items-center">
                    <CurrencyDollarIcon className="w-4 h-4 mr-1 text-accent-600" />
                    {user.storyCoins} StoryCoins
                  </div>
                  <div className="flex items-center">
                    <ShieldCheckIcon className="w-4 h-4 mr-1 text-green-600" />
                    Member since {new Date(user.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="card mb-8 overflow-hidden">
            <div className="border-b border-neutral-200 dark:border-neutral-700">
              <nav className="flex overflow-x-auto space-x-4 sm:space-x-8 px-4 sm:px-6 scrollbar-hide">
                {[
                  { id: 'profile', label: 'Profile', icon: UserCircleIcon },
                  { id: 'preferences', label: 'Preferences', icon: CogIcon },
                  { id: 'stats', label: 'Statistics', icon: ChartBarIcon },
                  { id: 'analytics', label: 'Analytics', icon: PresentationChartLineIcon },
                  { id: 'wallet', label: 'Wallet', icon: BanknotesIcon },
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id as typeof activeTab)}
                    className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap shrink-0 transition-colors ${
                      activeTab === id
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {label}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-4 sm:p-6 overflow-x-hidden">
              {activeTab === 'profile' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
                      Account Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={user.email}
                          disabled
                          className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                          Username
                        </label>
                        <input
                          type="text"
                          value={user.username}
                          disabled
                          className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                          Display Name
                        </label>
                        <input
                          type="text"
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 disabled:bg-neutral-50 dark:disabled:bg-neutral-700"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                          Role
                        </label>
                        <input
                          type="text"
                          value={user.role}
                          disabled
                          className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-neutral-50 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 capitalize"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'preferences' && (
                <div className="space-y-8">
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
                      <BellIcon className="w-5 h-5 inline mr-2" />
                      Notification Preferences
                    </h3>
                    <div className="space-y-4">
                      {Object.entries(preferences.notifications).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between">
                          <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                          </label>
                          <button
                            onClick={() => setPreferences(prev => ({
                              ...prev,
                              notifications: {
                                ...prev.notifications,
                                [key]: !value
                              }
                            }))}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              value ? 'bg-primary-600' : 'bg-neutral-200 dark:bg-neutral-700'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                value ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
                      Reading Preferences
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                          Theme
                        </label>
                        <select
                          value={preferences.theme}
                          onChange={(e) => {
                            const t = e.target.value as UserPreferences['theme']
                            setPreferences(prev => ({ ...prev, theme: t }))
                            setTheme(t)
                          }}
                          className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                        >
                          <option value="light">Light</option>
                          <option value="dark">Dark</option>
                          <option value="auto">Auto</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                          Font Size
                        </label>
                        <select
                          value={preferences.fontSize}
                          onChange={(e) => {
                            const fs = e.target.value as UserPreferences['fontSize']
                            setPreferences(prev => ({ ...prev, fontSize: fs }))
                            setReadingFontSize(FONT_SIZE_TO_UI[fs])
                          }}
                          className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                        >
                          <option value="small">Small</option>
                          <option value="medium">Medium</option>
                          <option value="large">Large</option>
                          <option value="extra-large">Extra Large</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
                      Privacy
                    </h3>
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                          Public Profile
                        </label>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                          Allow others to view your profile and published works
                        </p>
                      </div>
                      <button
                        onClick={async () => {
                          const newVisibility = user?.profileVisibility === 'private' ? 'public' : 'private'
                          await updateUserProfile({ profileVisibility: newVisibility })
                          showToast({ message: `Profile is now ${newVisibility}`, type: 'success' })
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          user?.profileVisibility !== 'private' ? 'bg-primary-600' : 'bg-neutral-200 dark:bg-neutral-700'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            user?.profileVisibility !== 'private' ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'stats' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
                    Account Statistics
                  </h3>
                  {stats ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div className="bg-primary-50 dark:bg-primary-900/20 p-6 rounded-lg">
                        <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                          {stats.branchesCount}
                        </div>
                        <div className="text-sm text-neutral-600 dark:text-neutral-400">
                          Branches Created
                        </div>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {stats.collaborationsCount}
                        </div>
                        <div className="text-sm text-neutral-600 dark:text-neutral-400">
                          Collaborations
                        </div>
                      </div>
                      <div className="bg-accent-50 dark:bg-accent-900/20 p-6 rounded-lg">
                        <div className="text-2xl font-bold text-accent-600 dark:text-accent-400">
                          {stats.purchasesCount}
                        </div>
                        <div className="text-sm text-neutral-600 dark:text-neutral-400">
                          Purchases Made
                        </div>
                      </div>
                      <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                          {stats.averageRating.toFixed(1)}
                        </div>
                        <div className="text-sm text-neutral-600 dark:text-neutral-400">
                          Average Rating
                        </div>
                      </div>
                      <div className="bg-orange-50 dark:bg-orange-900/20 p-6 rounded-lg">
                        <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                          {user.storyCoins}
                        </div>
                        <div className="text-sm text-neutral-600 dark:text-neutral-400">
                          StoryCoins Balance
                        </div>
                      </div>
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                          {stats.totalRatingCount}
                        </div>
                        <div className="text-sm text-neutral-600 dark:text-neutral-400">
                          Total Ratings
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <ChartBarIcon className="mx-auto h-12 w-12 text-neutral-400 mb-4" />
                      <p className="text-neutral-600 dark:text-neutral-400">
                        Loading statistics...
                      </p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'analytics' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
                    Author Analytics
                  </h3>
                  {analytics === undefined ? (
                    <div className="text-center py-12">
                      <PresentationChartLineIcon className="mx-auto h-12 w-12 text-neutral-400 mb-4" />
                      <p className="text-neutral-600 dark:text-neutral-400">Loading analytics…</p>
                    </div>
                  ) : !analytics || analytics.summary.branches === 0 ? (
                    <div className="text-center py-12 text-neutral-600 dark:text-neutral-400">
                      You haven't created any branches yet. Once you do, your reads, sales, and earnings show up here.
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {[
                          { label: 'Branches', value: analytics.summary.branches, cls: 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20' },
                          { label: 'Total reads', value: analytics.summary.totalReads, cls: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20' },
                          { label: 'Finished reads', value: analytics.summary.totalCompletedReads, cls: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20' },
                          { label: 'Sales', value: analytics.summary.totalPurchases, cls: 'text-accent-600 dark:text-accent-400 bg-accent-50 dark:bg-accent-900/20' },
                          { label: 'Earnings (SC)', value: analytics.summary.totalEarnings, cls: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20' },
                          { label: 'Avg rating', value: analytics.summary.avgRating.toFixed(1), cls: 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20' },
                          { label: 'Followers', value: analytics.summary.followers, cls: 'text-pink-600 dark:text-pink-400 bg-pink-50 dark:bg-pink-900/20' },
                          { label: 'Total words', value: analytics.summary.totalWords.toLocaleString(), cls: 'text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20' },
                        ].map((s) => (
                          <div key={s.label} className={`p-4 rounded-lg ${s.cls}`}>
                            <div className="text-2xl font-bold">{s.value}</div>
                            <div className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5">{s.label}</div>
                          </div>
                        ))}
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-neutral-700">
                              <th className="py-2 pr-3 font-semibold">Branch</th>
                              <th className="py-2 px-3 font-semibold text-right">Reads</th>
                              <th className="py-2 px-3 font-semibold text-right">Finished</th>
                              <th className="py-2 px-3 font-semibold text-right">Sales</th>
                              <th className="py-2 px-3 font-semibold text-right">Earnings</th>
                              <th className="py-2 pl-3 font-semibold text-right">Rating</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.branches.map((b) => (
                              <tr key={b._id} className="border-b border-neutral-100 dark:border-neutral-800">
                                <td className="py-2 pr-3">
                                  <Link to={`/reading/${b._id}`} className="text-primary-600 dark:text-primary-400 hover:underline font-medium">
                                    {b.title}
                                  </Link>
                                  {b.aiGenerated && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 font-semibold">AI</span>}
                                  <span className="block text-[11px] text-neutral-400">{b.bookTitle} · {b.visibility}</span>
                                </td>
                                <td className="py-2 px-3 text-right tabular-nums">{b.reads}</td>
                                <td className="py-2 px-3 text-right tabular-nums">{b.completedReads}</td>
                                <td className="py-2 px-3 text-right tabular-nums">{b.purchases}</td>
                                <td className="py-2 px-3 text-right tabular-nums">{b.earnings} SC</td>
                                <td className="py-2 pl-3 text-right tabular-nums">{b.ratingCount > 0 ? `${b.rating.toFixed(1)} (${b.ratingCount})` : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-xs text-neutral-400">Earnings are net StoryCoins (after the 25% platform fee). "Reads" counts readers who opened a branch.</p>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'wallet' && (
                <div className="space-y-8 overflow-x-hidden">
                  {/* Balance card */}
                  <div className="bg-gradient-to-br from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-xl p-6 border border-primary-100 dark:border-primary-800">
                    <p className="text-xs font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-wide mb-1">Total balance</p>
                    <p className="text-4xl font-bold text-primary-700 dark:text-primary-300">{user.storyCoins.toLocaleString()} <span className="text-lg font-medium">SC</span></p>
                    <p className="text-sm text-primary-600 dark:text-primary-400 mt-1">≈ £{(user.storyCoins / 100).toFixed(2)} &nbsp;·&nbsp; Available to spend on content</p>
                  </div>

                  {/* Breakdown cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
                      <p className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide mb-1">Earned</p>
                      <p className="text-xl font-bold text-green-700 dark:text-green-300">{(earnings?.totalEarned ?? 0).toLocaleString()}</p>
                      <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">From sales (net)</p>
                    </div>
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                      <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1">Purchased</p>
                      <p className="text-xl font-bold text-blue-700 dark:text-blue-300">{(earnings?.totalPurchased ?? 0).toLocaleString()}</p>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">Via Stripe</p>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4">
                      <p className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wide mb-1">Spent</p>
                      <p className="text-xl font-bold text-red-700 dark:text-red-300">{(earnings?.totalSpent ?? 0).toLocaleString()}</p>
                      <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">Unlocking content</p>
                    </div>
                    <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4">
                      <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-1">Withdrawable</p>
                      <p className="text-xl font-bold text-amber-700 dark:text-amber-300">{(earnings?.storybucksAvailable ?? 0).toLocaleString()}</p>
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                        {(earnings?.storybucksAvailable ?? 0) > 0
                          ? `≈ £${earnings?.gbpAvailable ?? '0.00'} from earnings`
                          : 'Only earned SC can be withdrawn'}
                      </p>
                    </div>
                  </div>

                  {/* Buy StoryCoins */}
                  <div className="border border-neutral-200 dark:border-neutral-700 rounded-xl p-5">
                    <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2 mb-1">
                      <PlusCircleIcon className="w-5 h-5 text-blue-500" />
                      Buy StoryCoins
                    </h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
                      100 StoryCoins = £1 &nbsp;·&nbsp; Paid securely via Stripe
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {[100, 500, 1000, 2500, 5000].map((amt) => (
                        <button
                          key={amt}
                          onClick={() => setBuyAmount(amt)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                            buyAmount === amt
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                              : 'border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:border-neutral-300'
                          }`}
                        >
                          {amt} SC · £{(amt / 100).toFixed(2)}
                        </button>
                      ))}
                    </div>
                    <button
                      disabled={isBuying}
                      onClick={async () => {
                        setIsBuying(true)
                        try {
                          const { url } = await createCheckoutSession({ amount: buyAmount })
                          if (url) {
                            window.location.href = url
                          } else {
                            throw new Error('Failed to create checkout session')
                          }
                        } catch (e) {
                          showToast({ message: e instanceof Error ? e.message : 'Purchase failed', type: 'error' })
                          setIsBuying(false)
                        }
                      }}
                      className="btn btn-primary"
                    >
                      {isBuying ? 'Processing…' : `Buy ${buyAmount} StoryCoins for £${(buyAmount / 100).toFixed(2)}`}
                    </button>
                    <p className="text-xs text-neutral-400 mt-2">💳 Powered by Stripe · Secure payment</p>
                  </div>

                  {/* Withdrawal via Stripe Connect */}
                  <div className="border border-neutral-200 dark:border-neutral-700 rounded-xl p-5">
                    <h3 className="font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2 mb-1">
                      <ArrowDownTrayIcon className="w-5 h-5 text-green-500" />
                      Withdraw earnings
                    </h3>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
                      Minimum £10 (1,000 StoryCoins) &nbsp;·&nbsp; No additional fees — the 25% platform fee was already deducted when you earned
                    </p>

                    {/* Connect onboarding status */}
                    {!connectStatus || !connectStatus.onboarded ? (
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 mb-4">
                        <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                          {connectStatus?.hasAccount
                            ? '⏳ Your Stripe account setup is incomplete. Please finish onboarding to enable withdrawals.'
                            : '🏦 To withdraw earnings, you need to set up a Stripe account. This is a one-time setup where you verify your identity and add your bank details.'}
                        </p>
                        <button
                          disabled={connectLoading}
                          onClick={async () => {
                            setConnectLoading(true)
                            try {
                              const { url } = await createConnectAccount()
                              if (url) window.location.href = url
                            } catch (e) {
                              showToast({ message: e instanceof Error ? e.message : 'Failed to start setup', type: 'error' })
                            } finally {
                              setConnectLoading(false)
                            }
                          }}
                          className="btn btn-primary text-sm"
                        >
                          {connectLoading ? 'Loading…' : connectStatus?.hasAccount ? 'Complete Stripe setup' : 'Set up Stripe account'}
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 mb-4 text-sm text-green-600 dark:text-green-400">
                          <BanknotesIcon className="w-4 h-4" />
                          <span>Stripe account connected</span>
                          <button
                            onClick={async () => {
                              try {
                                const { url } = await getConnectDashboardLink()
                                if (url) window.open(url, '_blank')
                              } catch (e) {
                                showToast({ message: 'Could not open dashboard', type: 'error' })
                              }
                            }}
                            className="ml-auto text-xs text-primary-600 dark:text-primary-400 hover:underline"
                          >
                            View Stripe dashboard →
                          </button>
                        </div>
                        <div className="mb-4">
                          <p className="text-sm text-neutral-700 dark:text-neutral-300 mb-2">
                            Available: <span className="font-semibold">£{((earnings?.storybucksAvailable ?? 0) / 100).toFixed(2)}</span>
                            <span className="text-neutral-400 ml-1">({earnings?.storybucksAvailable ?? 0} SC)</span>
                          </p>
                          <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Amount to withdraw (£)</label>
                          <div className="relative w-full max-w-xs">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 font-medium">£</span>
                            <input
                              type="number"
                              min={10}
                              step={1}
                              placeholder="min 10.00"
                              value={withdrawAmount}
                              onChange={(e) => setWithdrawAmount(e.target.value)}
                              className="w-full pl-7 pr-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                            />
                          </div>
                          {withdrawAmount && Number(withdrawAmount) >= 10 && (
                            <p className="text-xs text-green-600 mt-1">{withdrawSC} StoryCoins — paid directly to your bank</p>
                          )}
                          {withdrawAmount && Number(withdrawAmount) > 0 && Number(withdrawAmount) < 10 && (
                            <p className="text-xs text-red-500 mt-1">Minimum withdrawal is £10.00</p>
                          )}
                          {withdrawAmount && withdrawSC > (earnings?.storybucksAvailable ?? 0) && (
                            <p className="text-xs text-red-500 mt-1">Exceeds your available balance</p>
                          )}
                        </div>
                        <button
                          disabled={isWithdrawing || !withdrawAmount || Number(withdrawAmount) < 10 || withdrawSC > (earnings?.storybucksAvailable ?? 0)}
                          onClick={async () => {
                            setIsWithdrawing(true)
                            try {
                              const result = await processWithdrawal({ storybucksAmount: withdrawSC })
                              showToast({ message: `£${result.gbpAmount.toFixed(2)} sent to your Stripe account!`, type: 'success' })
                              setWithdrawAmount('')
                            } catch (e) {
                              showToast({ message: e instanceof Error ? e.message : 'Withdrawal failed', type: 'error' })
                            } finally {
                              setIsWithdrawing(false)
                            }
                          }}
                          className="btn btn-primary"
                        >
                          {isWithdrawing ? 'Processing…' : withdrawAmount && Number(withdrawAmount) >= 10 ? `Withdraw £${Number(withdrawAmount).toFixed(2)}` : 'Withdraw via Stripe'}
                        </button>
                      </>
                    )}
                  </div>

                  {/* Withdrawal history */}
                  {withdrawals && withdrawals.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-neutral-900 dark:text-neutral-100 mb-3">Withdrawal history</h4>
                      <div className="space-y-2">
                        {withdrawals.map((w) => (
                          <div key={w._id} className="flex flex-wrap items-center justify-between gap-2 py-3 px-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg text-sm">
                            <div className="min-w-0">
                              <span className="font-medium">{w.storybucksAmount} SC</span>
                              <span className="text-neutral-500 ml-2">· £{w.gbpAmount.toFixed(2)}</span>
                              <span className="text-neutral-400 text-xs ml-2">{new Date(w.createdAt).toLocaleDateString()}</span>
                            </div>
                            <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold ${
                              w.status === 'paid' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                              : w.status === 'approved' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                              : w.status === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                            }`}>
                              {w.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Transaction history */}
                  {earnings?.transactions && earnings.transactions.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-neutral-900 dark:text-neutral-100 mb-3">Transaction history</h4>
                      <div className="space-y-2">
                        {earnings.transactions.slice(0, 20).map((t) => (
                          <div key={t._id} className="flex items-center justify-between py-3 px-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg text-sm">
                            <div className="min-w-0">
                              <p className="font-medium text-neutral-900 dark:text-neutral-100 truncate">{t.description}</p>
                              <p className="text-xs text-neutral-400">{new Date(t.createdAt).toLocaleDateString()}</p>
                            </div>
                            <span className={`font-semibold ml-4 shrink-0 ${
                              t.amount > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                            }`}>
                              {t.amount > 0 ? '+' : ''}{t.amount} SC
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Avatar Crop Modal */}
      {cropImageSrc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 pt-5 pb-3">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Crop your photo</h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Drag to reposition, scroll to zoom</p>
            </div>
            <div className="relative w-full" style={{ height: 320 }}>
              <Cropper
                image={cropImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="px-6 py-2">
              <label className="text-xs text-neutral-500 dark:text-neutral-400">Zoom</label>
              <input
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-full accent-primary-600"
              />
            </div>
            <div className="flex justify-end gap-3 px-6 pb-5 pt-2">
              <button
                onClick={handleCropCancel}
                disabled={isUploadingAvatar}
                className="px-4 py-2 text-sm font-medium rounded-lg text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCropConfirm}
                disabled={isUploadingAvatar}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isUploadingAvatar && (
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
                )}
                {isUploadingAvatar ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProfilePage
